import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { commonStyles } from '~/modules/covenants/CovenantsDataForm/commonStyles.ts';
import {
  GUARANTEES_APPLICATION_AM_REVIEW_MODE,
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM,
  GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import {
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesManagerTerms,
  selectGuaranteesManagerTermsError,
  selectGuaranteesManagerTermsStatus,
  selectGuaranteesPatchManagerTermsError,
  selectGuaranteesPatchManagerTermsStatus,
  selectGuaranteesSubmitApplicationByManagerError,
  selectGuaranteesSubmitApplicationByManagerStatus,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesApplication,
  fetchGuaranteesManagerTerms,
  patchGuaranteesManagerTerms,
  submitGuaranteesApplicationByManager,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
  type TPatchGuaranteesManagerTermsPayload,
} from '~/store/guarantees/types.ts';
import {
  getGuaranteesPayloadVersion,
  normalizeGuaranteesApplicationStatus,
} from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useDispatch } from 'react-redux';
import ApplicationHeader from '../../shared/components/ApplicationHeader.tsx';
import ApplicationPageFrame from '../../shared/components/ApplicationPageFrame.tsx';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import {
  getStringValue,
  isManualAmApprovalLetterInProgress,
} from '~/modules/guarantees/utils.ts';
import ManagerTermsSection from './ManagerTermsSection.tsx';
import { MANAGER_TERMS_TEXT } from './constants.ts';
import GuaranteesManagerStepNavigator from '../stepNavigation/GuaranteesManagerStepNavigator.tsx';
import GuaranteesManagerApplicationSidePanelLayout from '../shared/SidePanel/GuaranteesManagerApplicationSidePanelLayout.tsx';

const managerTermsEditableStatuses = new Set<GuaranteesApplicationStatus>([
  GuaranteesApplicationStatus.AWAITING_PM_MANAGER_TERMS,
  GuaranteesApplicationStatus.IN_PROGRESS_PM_MANAGER_TERMS,
]);

const ManagerTermsStep2Page: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const isAmReviewMode =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM) ===
    GUARANTEES_APPLICATION_AM_REVIEW_MODE;
  const isManualTaskMode =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM) ===
    'true';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const managerTermsFromStore = useSelector(selectGuaranteesManagerTerms);
  const managerTermsStatus = useSelector(selectGuaranteesManagerTermsStatus);
  const managerTermsError = useSelector(selectGuaranteesManagerTermsError);
  const patchManagerTermsStatus = useSelector(selectGuaranteesPatchManagerTermsStatus);
  const patchManagerTermsError = useSelector(selectGuaranteesPatchManagerTermsError);
  const submitByManagerStatus = useSelector(selectGuaranteesSubmitApplicationByManagerStatus);
  const submitByManagerError = useSelector(selectGuaranteesSubmitApplicationByManagerError);
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const redirectPath = !contragentId ? '/guarantees/administrator/get-started' : '';
  const managerTerms = isLoadedApplicationCurrent
    ? managerTermsFromStore ?? currentRouteApplication?.managerTerms ?? null
    : null;
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number' ? currentRouteApplication.version : 0;
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const isManagerTermsLoading = managerTermsStatus === GuaranteesFetchStatus.Loading;
  const isSavingManagerTerms = patchManagerTermsStatus === GuaranteesFetchStatus.Loading;
  const isSubmittingByManager = submitByManagerStatus === GuaranteesFetchStatus.Loading;
  const canEditManualAmApprovalLetterInProgress =
    isAmReviewMode && isManualAmApprovalLetterInProgress(currentRouteApplication, isManualTaskMode);
  const isManagerTermsEditable =
    managerTermsEditableStatuses.has(normalizedStatus) || canEditManualAmApprovalLetterInProgress;
  const isManagerTermsSectionDisabled =
    !currentApplicationVersion ||
    (isAmReviewMode && !canEditManualAmApprovalLetterInProgress) ||
    !isManagerTermsEditable;
  const requestError =
    applicationError || managerTermsError || patchManagerTermsError || submitByManagerError;
  const {
    modalAlert,
    modalSuccess,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: () => navigate('/tasks'),
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigate, statusConfig]
  );
  const navigateToTasks = useCallback(() => {
    navigate('/tasks', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
    void dispatch(fetchGuaranteesManagerTerms({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useFreshGuaranteesRequestError(requestError, showGuaranteesRequestError);

  const handleSaveManagerTerms = useCallback(
    async (payload: TPatchGuaranteesManagerTermsPayload) => {
      if (!contragentId || !applicationId || !currentApplicationVersion) {
        return null;
      }

      try {
        const patchedManagerTerms = await dispatch(
          patchGuaranteesManagerTerms({
            contragentId,
            applicationId,
            version: currentApplicationVersion,
            payload,
          })
        ).unwrap();
        setModalSuccess({
          text: MANAGER_TERMS_TEXT.savedMessage,
          buttonValue: 'Закрити',
        });
        return patchedManagerTerms;
      } catch (error) {
        showGuaranteesRequestError(error);
        throw error;
      }
    },
    [
      applicationId,
      contragentId,
      currentApplicationVersion,
      dispatch,
      setModalSuccess,
      showGuaranteesRequestError,
    ]
  );
  const handleConfirmManagerTerms = useCallback(
    async (payload: TPatchGuaranteesManagerTermsPayload) => {
      if (!contragentId || !applicationId || !effectiveApplicationId || !currentApplicationVersion) {
        return null;
      }

      try {
        const patchedManagerTerms = await dispatch(
          patchGuaranteesManagerTerms({
            contragentId,
            applicationId,
            version: currentApplicationVersion,
            payload,
          })
        ).unwrap();
        const submitVersion =
          getGuaranteesPayloadVersion(patchedManagerTerms) ?? currentApplicationVersion;

        await dispatch(
          submitGuaranteesApplicationByManager({
            applicationId: effectiveApplicationId,
            version: submitVersion,
          })
        ).unwrap();
        setModalSuccess({
          text: MANAGER_TERMS_TEXT.submittedToUdbtfMessage,
          buttonValue: 'Закрити',
          onClick: navigateToTasks,
          onClose: navigateToTasks,
        });

        return patchedManagerTerms;
      } catch (error) {
        showGuaranteesRequestError(error);
        throw error;
      }
    },
    [
      applicationId,
      contragentId,
      currentApplicationVersion,
      dispatch,
      effectiveApplicationId,
      navigateToTasks,
      setModalSuccess,
      showGuaranteesRequestError,
    ]
  );

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <ApplicationPageFrame
      modalAlert={modalAlert}
      modalSuccess={modalSuccess}
      onModalClose={handleCloseAlertModal}
      onSuccessModalClose={handleCloseSuccessModal}
    >
      <ApplicationHeader {...headerProps} />

      {isApplicationLoading ? (
        <Box sx={commonStyles.centeredLoader}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={guaranteesClientPageStyles.applicationFlowStack}>
          <GuaranteesManagerApplicationSidePanelLayout
            applicationId={effectiveApplicationId}
            onError={showGuaranteesRequestError}
          >
            <ManagerTermsSection
              managerTerms={managerTerms}
              isDisabled={isManagerTermsSectionDisabled}
              isConfirming={isSubmittingByManager}
              isLoading={isManagerTermsLoading}
              isSaving={isSavingManagerTerms}
              requireChangesToEnableActions
              onConfirm={handleConfirmManagerTerms}
              onSubmit={handleSaveManagerTerms}
            />
          </GuaranteesManagerApplicationSidePanelLayout>

          <GuaranteesManagerStepNavigator
            application={currentRouteApplication}
            applicationId={effectiveApplicationId}
            contragentId={contragentId}
            currentStepKey="step2"
          />
        </Box>
      )}
    </ApplicationPageFrame>
  );
};

export default ManagerTermsStep2Page;
